import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  Calendar, Eye, User, ArrowLeft, Share2, Heart,
  Bookmark, MessageCircle, ChevronUp, PrinterIcon,
  Facebook, Twitter, Mail, Copy
} from 'lucide-react';
import Markdown from 'markdown-to-jsx';

const NewsletterDetail = () => {
  const { id } = useParams();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const fetchNewsletter = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/newsletter/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) throw new Error('Newsletter not found');
        const data = await response.json();
        setNewsletter(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletter();
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = newsletter?.title;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`);
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
        break;
      case 'mail':
        window.open(`mailto:?subject=${title}&body=${url}`);
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-xl text-gray-600 mb-8">{error || 'Newsletter not found'}</p>
          <Link 
            to="/newsletters"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl
              hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Newsletters
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Fixed Action Bar */}
      <motion.div 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg shadow-sm z-50"
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/newsletters"
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Newsletters
          </Link>

          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsLiked(!isLiked)}
              className={`p-2 rounded-full ${
                isLiked ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Heart className={isLiked ? 'fill-current' : ''} size={20} />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsBookmarked(!isBookmarked)}
              className={`p-2 rounded-full ${
                isBookmarked ? 'bg-blue-50 text-blue-500' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Bookmark className={isBookmarked ? 'fill-current' : ''} size={20} />
            </motion.button>

            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="p-2 rounded-full bg-gray-100 text-gray-600"
              >
                <Share2 size={20} />
              </motion.button>

              <AnimatePresence>
                {showShareMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-2"
                  >
                    {[
                      { icon: Facebook, label: 'Facebook', platform: 'facebook' },
                      { icon: Twitter, label: 'Twitter', platform: 'twitter' },
                      { icon: Mail, label: 'Email', platform: 'mail' },
                      { icon: Copy, label: 'Copy Link', platform: 'copy' }
                    ].map((item) => (
                      <motion.button
                        key={item.platform}
                        whileHover={{ x: 4 }}
                        onClick={() => handleShare(item.platform)}
                        className="w-full px-4 py-2 flex items-center text-gray-700 hover:text-blue-600"
                      >
                        <item.icon size={16} className="mr-3" />
                        {item.label}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        {/* Newsletter Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {newsletter.coverImage && (
            <div className="relative h-96">
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
              <img
                src={newsletter.coverImage}
                alt={newsletter.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                    {newsletter.category}
                  </span>
                  <span className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(newsletter.createdAt).toLocaleDateString()}
                  </span>
                  <span className="flex items-center text-sm">
                    <Eye className="w-4 h-4 mr-2" />
                    {newsletter.viewCount} views
                  </span>
                </div>
                <h1 className="text-4xl font-bold mb-4">
                  {newsletter.title}
                </h1>
                {newsletter.subtitle && (
                  <p className="text-xl text-white/90">
                    {newsletter.subtitle}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <Markdown {...markdownOptions}>
                {newsletter.content || ''}
              </Markdown>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 p-4 bg-blue-600 text-white rounded-full 
              shadow-lg hover:bg-blue-700 transition-colors"
          >
            <ChevronUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

// Markdown rendering options
const markdownOptions = {
  options: {
    overrides: {
      h1: {
        component: props => (
          <h1 className="text-4xl font-bold text-gray-900 mb-8" {...props} />
        )
      },
      h2: {
        component: props => (
          <h2 className="text-3xl font-bold text-gray-900 mb-6" {...props} />
        )
      },
      h3: {
        component: props => (
          <h3 className="text-2xl font-bold text-gray-900 mb-4" {...props} />
        )
      },
      p: {
        component: props => (
          <p className="text-gray-700 leading-relaxed mb-6" {...props} />
        )
      },
      a: {
        component: props => (
          <a className="text-blue-600 hover:text-blue-700 underline" {...props} />
        )
      },
      ul: {
        component: props => (
          <ul className="list-disc pl-6 mb-6 space-y-2" {...props} />
        )
      },
      ol: {
        component: props => (
          <ol className="list-decimal pl-6 mb-6 space-y-2" {...props} />
        )
      },
      blockquote: {
        component: props => (
          <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 mb-6" {...props} />
        )
      },
      code: {
        component: props => (
          <code className="bg-gray-100 rounded px-2 py-1 text-sm font-mono" {...props} />
        )
      },
      pre: {
        component: props => (
          <pre className="bg-gray-900 text-white rounded-lg p-4 overflow-x-auto mb-6" {...props} />
        )
      }
    }
  }
};

export default NewsletterDetail;